import { DOCUMENT, Location } from '@angular/common';
import { Component, HostBinding, Inject, OnInit } from '@angular/core';
import { getIsAnimationTestEnvironment } from '@core/animation/animation';
import { BootstrapService } from '@core/bootstrap/bootstrap.service';
import { IConsent } from '@core/consent/consent.model';
import { ConsentService } from '@core/consent/consent.service';
import { LoggerService } from '@core/logger/logger.service';
import { NavigationService } from '@core/navigation/navigation.service';
import { PageTitleService } from '@core/page-title/page-title.service';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { WidthObserverService } from '@roller/rdl/utilities/width-observer';
import { combineLatest, filter, Observable, switchMap, take, tap } from 'rxjs';
import { InitialConfigService } from './core/app-config/initial-config.service';
import { CRouteList } from './route-list.const';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  @HostBinding('@.disabled')
  public isAnimationDisabled: boolean;

  isLoading$: Observable<boolean>;
  readonly cRoutes = CRouteList;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private location: Location,
    private widthObserverService: WidthObserverService,
    private navigationService: NavigationService,
    private pageTitleService: PageTitleService,
    private venueConfigService: VenueConfigService,
    private consentService: ConsentService,
    private bootstrapService: BootstrapService,
    private initialConfigService: InitialConfigService,
    private loggerService: LoggerService
  ) {
    this.isAnimationDisabled = getIsAnimationTestEnvironment();
    this.isLoading$ = this.bootstrapService.getIsLoading$();
  }

  ngOnInit(): void {
    if (getIsAnimationTestEnvironment()) {
      this.disableCSSAnimations();
    }

    const afterInitLifetimeObserve$ = [
      this.listenForRouteChanges(),
      this.widthObserverService.listenBreakpoints(),
      this.navigationService.listenToRouteStartEnd(this.document.location.origin)
    ];

    this.bootstrapService
      .getIsLoading$()
      .pipe(
        filter(() => this.bootstrapService.getIsAppInitialized()),
        take(1),
        tap(() => {
          this.checkForReferrerConsent();
          this.setHtmlPageLanguage();
          this.checkPolyfills();
        }),
        switchMap(() => combineLatest(afterInitLifetimeObserve$))
      )
      .subscribe();
  }

  /** Listens for route changes and notifies the page title service. */
  listenForRouteChanges(): Observable<string> {
    return this.navigationService.getPageUrl().pipe(
      tap((url: string) => {
        const venueConfig = this.venueConfigService.getVenueConfig();
        const currentRoute = this.navigationService.currentUrlName(url);
        this.pageTitleService.setTitle(venueConfig.venueSettings.name, currentRoute, url);
      })
    );
  }

  /**
   * Check the url for query params to set consent from a referrer,
   * deletes the now unneeded search param and then silently changes
   * the url to remove the consent ref query params.
   * @example query params - ?isPerformanceGranted=true&isMarketingGranted=true&ref={venuesHomeUrl}
   */
  checkForReferrerConsent(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams) {
      return;
    }

    const isMarketingGranted = url.searchParams.get('isMarketingGranted');
    const isPerformanceGranted = url.searchParams.get('isPerformanceGranted');
    const parentUrl = url.searchParams.get('ref') || '';
    if (!parentUrl || isMarketingGranted === null || isPerformanceGranted === null) {
      return;
    }

    const consent: IConsent = {
      venueSlug: '',
      lastUpdated: new Date(),
      consent: {
        isMarketingGranted: isMarketingGranted === 'true',
        isPerformanceGranted: isPerformanceGranted === 'true',
        parentUrl
      }
    };
    this.consentService.setCurrentConsent(consent);
    url.searchParams.delete('isMarketingGranted');
    url.searchParams.delete('isPerformanceGranted');
    url.searchParams.delete('ref');
    this.location.go(url.pathname, url.search);
  }

  /** For screen reader assistive devices the language must be programmatically determinable */
  private setHtmlPageLanguage(): void {
    this.document.documentElement.lang = this.initialConfigService.getInitialConfig().lang;
  }

  /** Attempt to check the window for the structuredClone polyfill and log it for internal statistics */
  private checkPolyfills() {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any)._structuredClonePolyfilledR$) {
        this.loggerService.info('structuredClone polyfill added after bootstrap success');
      }
    } catch {
      /* continue */
    }
  }

  private disableCSSAnimations(): void {
    this.document.body.classList.add('app-disable-animations');
  }
}
